const COOKIE_NAME = "geolocator_seen";
const COOKIE_MAX_AGE = 86400;
const GEO_API = "https://get.geojs.io/v1/ip/country.json";

function hasSeenGeolocator() {
  return document.cookie
    .split(";")
    .some((entry) => entry.trim().startsWith(`${COOKIE_NAME}=`));
}

function setGeolocatorSeen() {
  document.cookie = `${COOKIE_NAME}=1; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
}

function isCookieConsentTarget(target) {
  if (!target) {
    return false;
  }

  if (target.tagName === "IFRAME") {
    const src = target.getAttribute("src") || "";

    if (/cookie-script\.com|cookiescript/i.test(src)) {
      return true;
    }
  }

  return !!target.closest?.(
    '#cookiescript_injected, #cookiescript_badge, .cookiescript_injected, [id^="cookiescript_"], [class*="cookiescript"], privacy-banner, .privacy-banner'
  );
}

function protectModalFromCookieBanner(modal) {
  const hideForOutsideClick = modal.hideForOutsideClickTarget.bind(modal);
  const allowOutsideClick = modal.allowOutsideClickForTarget.bind(modal);

  modal.hideForOutsideClickTarget = (target) => {
    if (isCookieConsentTarget(target)) {
      return false;
    }

    return hideForOutsideClick(target);
  };

  modal.allowOutsideClickForTarget = (target) => {
    if (isCookieConsentTarget(target)) {
      return true;
    }

    return allowOutsideClick(target);
  };
}

function getSelectedCountryOption(picker) {
  return picker?.querySelector('[role="option"][aria-selected="true"]');
}

function syncSelectionFromOption(form, picker, toggleFlag, toggleLabel) {
  const option = getSelectedCountryOption(picker);
  const countryInput = picker?.querySelector('input[name="country_code"]');
  const localeInput = form?.querySelector('input[name="locale_code"]');

  if (countryInput && option?.value) {
    countryInput.value = option.value;
  }

  if (localeInput && option?.dataset.locale) {
    localeInput.value = option.dataset.locale;
  }

  if (toggleLabel && option?.title) {
    toggleLabel.textContent = option.title;
  }

  if (toggleFlag && option?.dataset.flagSrc) {
    toggleFlag.src = option.dataset.flagSrc;
    toggleFlag.alt = option.title || "";
  }
}

function selectCountryOption(picker, countryCode, form, toggleFlag, toggleLabel) {
  const option = picker?.querySelector(
    `[role="option"][value="${CSS.escape(countryCode)}"]`
  );

  if (!option) {
    return;
  }

  picker.querySelectorAll('[role="option"]').forEach((entry) => {
    entry.setAttribute(
      "aria-selected",
      entry === option ? "true" : "false"
    );
  });

  syncSelectionFromOption(form, picker, toggleFlag, toggleLabel);
}

class GeolocatorModal extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector("x-modal");
    this.form = this.querySelector("form");
    this.picker = this.querySelector(".geolocator-country-picker");
    this.toggle = this.querySelector(".geolocator-country-toggle");
    this.list = this.querySelector(".geolocator-country-picker__list");
    this.toggleFlag = this.querySelector("[data-geolocator-flag]");
    this.toggleLabel = this.querySelector("[data-geolocator-label]");
    this.countryInput = this.picker?.querySelector('input[name="country_code"]');
    this.currentCountry = this.dataset.country?.toUpperCase();

    if (!this.modal || hasSeenGeolocator()) {
      return;
    }

    protectModalFromCookieBanner(this.modal);

    this.redirecting = false;
    this.handleDocumentClick = this.handleDocumentClick.bind(this);

    this.modal.addEventListener("dialog:after-hide", () => {
      this.closePicker();
      if (!this.redirecting) {
        setGeolocatorSeen();
      }
    });

    this.toggle?.addEventListener("click", () => {
      this.togglePicker();
    });

    this.list?.querySelectorAll('[role="option"]').forEach((option) => {
      option.addEventListener("click", () => {
        this.picker.querySelectorAll('[role="option"]').forEach((entry) => {
          entry.setAttribute(
            "aria-selected",
            entry === option ? "true" : "false"
          );
        });
        syncSelectionFromOption(
          this.form,
          this.picker,
          this.toggleFlag,
          this.toggleLabel
        );
        this.closePicker();
      });
    });

    this.form?.addEventListener("submit", () => {
      syncSelectionFromOption(
        this.form,
        this.picker,
        this.toggleFlag,
        this.toggleLabel
      );

      const selectedCountry = this.countryInput?.value?.toUpperCase();
      this.redirecting =
        !!selectedCountry && selectedCountry !== this.currentCountry;

      if (!this.redirecting) {
        setGeolocatorSeen();
      }
    });

    this.init();
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  openPicker() {
    if (!this.picker || !this.list || !this.toggle) {
      return;
    }

    this.list.hidden = false;
    this.toggle.setAttribute("aria-expanded", "true");
    this.picker.classList.add("geolocator-country-picker--open");
    document.addEventListener("click", this.handleDocumentClick);
  }

  closePicker() {
    if (!this.picker || !this.list || !this.toggle) {
      return;
    }

    this.list.hidden = true;
    this.toggle.setAttribute("aria-expanded", "false");
    this.picker.classList.remove("geolocator-country-picker--open");
    document.removeEventListener("click", this.handleDocumentClick);
  }

  togglePicker() {
    if (this.list?.hidden) {
      this.openPicker();
    } else {
      this.closePicker();
    }
  }

  handleDocumentClick(event) {
    if (isCookieConsentTarget(event.target)) {
      return;
    }

    if (!this.picker?.contains(event.target)) {
      this.closePicker();
    }
  }

  async init() {
    try {
      const response = await fetch(GEO_API);

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const detectedCountry = data?.country?.toUpperCase();

      if (!detectedCountry || detectedCountry === this.currentCountry) {
        return;
      }

      selectCountryOption(
        this.picker,
        detectedCountry,
        this.form,
        this.toggleFlag,
        this.toggleLabel
      );

      await this.modal.show();
    } catch {
      return;
    }
  }
}

if (!window.customElements.get("geolocator-modal")) {
  window.customElements.define("geolocator-modal", GeolocatorModal);
}
